/**
 * Lobby.tsx —— 房间大厅：展示房间号、座位、准备状态；房主可开局。
 *
 * 第 16 轮设计还原（D-S1 / D-S2 / D-N3）：
 *   - 毛毡底色 + 简化 Table 形态，宽屏下席位**环形**排列（与牌桌镜像一致），窄屏回落为列表；
 *   - 席位卡带玩家头像；房主用 Crown 图标标识（不再是文字胶囊）；
 *   - 席位加入/离开带淡入动效。
 */

import { useState } from 'react';
import { mpClient, type MpState } from '../../net/mpClient';
import { pushToast } from '../../state/toasts';
import { chips } from '../../state/format';
import { MpAvatar, CrownIcon, PresenceBadge } from './mpVisuals';

export function Lobby({ state, onLeave }: { state: MpState; onLeave: () => void }) {
  const room = state.lobby;
  const [copied, setCopied] = useState(false);
  if (!room) return <div className="mp-loading">正在进入房间…</div>;

  const me = room.seats.find((s) => s.id === room.youId) ?? null;
  const isHost = room.hostId === room.youId;
  const seatedCount = room.seats.filter((s) => s.id).length;

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(room.code); setCopied(true); pushToast('房间号已复制', 'success'); setTimeout(() => setCopied(false), 1500); }
    catch { pushToast('复制失败，请手动记下房间号', 'warning'); }
  };

  const n = room.seats.length;

  return (
    <div className="mp-lobby">
      <header className="mp-lobby__head">
        <button className="mp-btn mp-btn--ghost" onClick={onLeave}>← 离开</button>
        <div className="mp-code" onClick={copyCode} role="button" tabIndex={0} title="点击复制房间号">
          <span className="mp-code__label">房间号</span>
          <span className="mp-code__value">{room.code}</span>
          <span className="mp-code__copy">{copied ? '已复制 ✓' : '点击复制'}</span>
        </div>
      </header>

      <div className="mp-lobby__meta">
        <span>{seatedCount}/{room.maxSeats} 人</span>
        <span>盲注 {room.config.smallBlind}/{room.config.bigBlind}</span>
        <span>带入 {chips(room.config.startingStack)}</span>
        <span>{room.config.actionTimeoutSec > 0 ? `${room.config.actionTimeoutSec}s 时限` : '不限时'}</span>
      </div>

      {/* 简化 Table：毛毡桌面 + 环形席位（宽屏）／列表（窄屏） */}
      <div className="mp-lobby__table" data-seats={n}>
        <div className="mp-lobby__felt">
          <span className="mp-lobby__brand">Riverside</span>
          <span className="mp-lobby__felt-code">房间 {room.code}</span>
        </div>
        <ul className="mp-seats" data-seats={n}>
          {room.seats.map((s, i) => {
            const isMe = s.id === room.youId;
            // 环形（椭圆）坐标：从正上方顺时针铺开，用百分比定位保证不溢出容器/不遮挡底部按钮
            const rad = ((360 / n) * i - 90) * (Math.PI / 180);
            const style = {
              '--seat-x': `${(50 + 40 * Math.cos(rad)).toFixed(2)}%`,
              '--seat-y': `${(50 + 40 * Math.sin(rad)).toFixed(2)}%`,
            } as React.CSSProperties;
            return (
              <li
                key={s.seat}
                style={style}
                className={`mp-seat ${s.id ? 'mp-seat--filled' : 'mp-seat--empty'} ${isMe ? 'mp-seat--me' : ''}`}
              >
                {s.id ? (
                  <>
                    <MpAvatar name={s.name} seat={s.seat} />
                    <span className="mp-seat__info">
                      <span className="mp-seat__name">
                        {s.name}{isMe && ' (你)'}
                        {s.isHost && <CrownIcon />}
                        <PresenceBadge connected={s.connected} presence={s.presence} />
                      </span>
                      <span className={`mp-seat__ready ${s.ready ? 'is-ready' : ''}`}>{s.ready ? '已准备' : '未准备'}</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="mp-seat__avatar-empty" aria-hidden="true" />
                    <span className="mp-seat__info">
                      <span className="mp-seat__no">座位 {s.seat + 1}</span>
                      <button className="mp-btn mp-btn--tiny" onClick={() => mpClient.sit(s.seat)} disabled={!!me}>
                        {me ? '空位' : '坐这里'}
                      </button>
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <footer className="mp-lobby__foot">
        {me && (
          <button
            className={`mp-btn ${me.ready ? 'mp-btn--ghost' : 'mp-btn--primary'}`}
            onClick={() => mpClient.setReady(!me.ready)}
          >
            {me.ready ? '取消准备' : '准备'}
          </button>
        )}
        {isHost && (
          <button
            className="mp-btn mp-btn--primary"
            disabled={!room.canStart}
            onClick={() => mpClient.start()}
            title={room.canStart ? '' : '需要 ≥2 名玩家且全部已准备'}
          >
            开始对局
          </button>
        )}
        {!isHost && <span className="mp-hint">等待房主开始对局…</span>}
      </footer>
      {isHost && !room.canStart && (
        <p className="mp-hint mp-hint--center">至少 2 名玩家、且所有人已点击「准备」后即可开局。</p>
      )}
    </div>
  );
}
